import { Box, Paper } from "@mui/material";
import styles from "./MyInboxPublications.module.scss";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PaginatedDataTable } from "../../../components/Table";
import { GridColDef } from "@mui/x-data-grid";
import StageComponent from "../StageComponent";
import workflowService from "../../../services/workflow.service";
import {
  ICanStartWorfklow,
  Publication,
  WorkflowType,
} from "../../../models/workflow.model";
import BackButton from "../../../components/BackButton";
import { getLabel } from "../../../misc/utils.functions";
import moment from "moment";
import { PagedFilter } from "../../../misc/pagedFilter";
import DocumentDownload from "../../../components/DocumentDownload";
import GenericStatusComponent from "../../../components/StatusComponent";
import AvatarList from "../../../components/AvatarList";
import StatusComponent from "../StatusComponent";
import ActionsMenu from "../Search/ActionsMenu";
import ModifyDialog from "../Modify/ModifyDialog";
import WorkflowTypeComponent from "../Search/WorkflowType";
import CancelWorkflowDialog from "../CancelWorkflowDialog";
import MainContext from "../../../context/mainContext";
import { RoleCode } from "../../../models/user.model";
import RepublishWarningDialog from "../RepublishWarningDialog";

interface Props {
  status: string;
  refresh: boolean;
  showBack?: boolean;
  showWorkflowTypesReference?: boolean;
  onRefreshAction: () => void;
}

const MyInboxPublications: React.FC<Props> = ({
  status,
  refresh,
  onRefreshAction,
  showBack,
  showWorkflowTypesReference,
}) => {
  const { t } = useTranslation();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(false);
  const title = t(`publicationStatus.${status}`);
  const navigate = useNavigate();
  const [modifySelected, setModifySelected] = useState<Publication>();
  const [modifyType, setModifyType] = useState<WorkflowType>("Modification");

  const initFilter: PagedFilter = {
    pageNumber: 1,
    pageSize: 5,
    sortField: "createdDate",
    sortDescending: true,
  };

  const [currentFilter, setCurrentFilter] = useState<PagedFilter>(initFilter);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [cancelDialogShow, setCancelDialogShow] = useState(false);
  const [currentPublication, setCurrentPublication] =
    useState<Publication | null>(null);

  const { currentUser, service } = useContext(MainContext);

  const isCommunicationTeam = currentUser?.roles.includes(
    RoleCode.Communications
  );

  const [canRepublishChange, setCanRepublishChange] =
    useState<ICanStartWorfklow>();

  const handleModifyAction = (
    publication: Publication | undefined,
    type: WorkflowType
  ) => {
    setModifyType(type);
    setModifySelected(publication);
  };

  const handleModifyCancel = () => {
    setModifyType("Modification");
    setModifySelected(undefined);
  };

  const handleContinueAction = (publication: Publication | undefined) => {
    if (publication !== undefined) {
      navigate(`/workflow/${publication.ticketNumber}`);
    }
  };

  const handleCancelAction = (publication: Publication) => {
    setCancelDialogShow(true);
    setCurrentPublication(publication);
  };

  const cancelPublication = () => {
    setCancelDialogShow(false);
    if (currentPublication !== null) {
      setLoading(true);
      workflowService
        .cancelPublication(currentPublication.ticketNumber)
        .then(() => {
          setCurrentPublication(null);
          setLoading(false);
          onRefreshAction();
        });
    }
  };

  const handleViewSummaryAction = (publication: Publication | undefined) => {
    if (publication !== undefined) {
      navigate(`/summary/${publication.ticketNumber}`);
    }
  };

  const handleMinorChangesAction = (publication: Publication | undefined) => {
    if (publication !== undefined) {
      service(
        () => workflowService.getCanModifiyWorkflow(publication.ticketNumber),
        {
          callback: (data) => {
            if (data?.canStart) {
              setCanRepublishChange(undefined);
              navigate(`/republish/${publication.ticketNumber}`);
            } else {
              setCanRepublishChange(data);
            }
          },
          loader: setLoading,
        }
      );
    }
  };

  const inProgressColumns: GridColDef[] = [
    {
      field: "ticketNumber",
      headerName: getLabel("ticketNumber"),
      sortable: true,
      width: 170,
      renderCell: (params) => {
        return (
          <p className={styles.ticketNumberCell}>
            <WorkflowTypeComponent type={params.row.workflowType} />{" "}
            {params.row.ticketNumber}
          </p>
        );
      },
    },
    {
      field: "title",
      headerName: getLabel("title"),
      sortable: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "createdBy",
      headerName: getLabel("createdBy"),
      sortable: true,
      width: 140,
    },
    {
      field: "createdDate",
      headerName: getLabel("createdDate"),
      sortable: true,
      width: 140,
      valueFormatter: (params) => moment(params?.value).format("MM/DD/YYYY"),
    },
    {
      field: "stage",
      headerName: "Stage",
      sortable: false,
      width: 120,
      renderCell: (params) => {
        return (
          <StageComponent
            workflowType={params.row.workflowType}
            stageCode={params.row.stageCode}
            stageStep={params.row.stageStep}
            isCompleted={params.row.isCompleted}
            waitingApproval={params.row.waitingApproval}
          />
        );
      },
    },
    {
      field: "peerReviews",
      headerName: getLabel("peerReviews"),
      sortable: false,
      width: 180,
      renderCell: (params) => {
        if (
          params.row.peerReviews !== null &&
          params.row.peerReviews.length > 0
        ) {
          return <AvatarList users={params.row.peerReviews} maxAvatars={3} />;
        } else {
          return params.row.workflowType !== "Delete" &&
            (params.row.peerReviewRequired === null ||
              params.row.peerReviewRequired) ? (
            <GenericStatusComponent text={"pending"} />
          ) : (
            <GenericStatusComponent text={"na"} />
          );
        }
      },
    },
    {
      field: "approvals",
      headerName: getLabel("approvals"),
      sortable: false,
      width: 180,
      renderCell: (params) => {
        if (params.row.approvals !== null && params.row.approvals.length > 0) {
          return <AvatarList users={params.row.approvals} maxAvatars={3} />;
        } else {
          return <GenericStatusComponent text={"pending"} />;
        }
      },
    },
    {
      field: "document",
      headerName: getLabel("document"),
      headerClassName: "document",
      sortable: false,
      cellClassName: "document",
      width: 100,
      renderCell: (params) => {
        return params.row.documents != null ? (
          <DocumentDownload documents={params.row.documents} />
        ) : (
          <></>
        );
      },
    },
    {
      field: "actions",
      headerName: getLabel("actions"),
      headerClassName: "actions",
      sortable: false,
      cellClassName: "actions",
      width: 100,
      renderCell: (params) => {
        return (
          <ActionsMenu
            status={params.row.status}
            isOwner={true}
            continueAction={() => handleContinueAction(params.row)}
            modifyAction={() => handleModifyAction(params.row, "Modification")}
            deleteAction={() => handleModifyAction(params.row, "Delete")}
            cancelAction={() => handleCancelAction(params.row)}
          />
        );
      },
    },
  ];

  const completedColumns: GridColDef[] = [
    {
      field: "ticketNumber",
      headerName: getLabel("ticketNumber"),
      sortable: true,
      width: 170,
      renderCell: (params) => {
        return (
          <p className={styles.ticketNumberCell}>
            <WorkflowTypeComponent type={params.row.workflowType} />{" "}
            {params.row.ticketNumber}
          </p>
        );
      },
    },
    {
      field: "title",
      headerName: getLabel("title"),
      sortable: true,
      flex: 2,
      minWidth: 150,
    },
    {
      field: "createdBy",
      headerName: getLabel("createdBy"),
      sortable: true,
      flex: 1,
      width: 140,
    },
    {
      field: "createdDate",
      headerName: getLabel("createdDate"),
      sortable: true,
      flex: 1,
      width: 140,
      valueFormatter: (params) => moment(params?.value).format("MM/DD/YYYY"),
    },
    {
      field: "status",
      headerName: getLabel("status"),
      sortable: false,
      flex: 1,
      width: 140,
      renderCell: (params) => {
        return <StatusComponent statusCode={params.row.status} />;
      },
    },
    {
      field: "document",
      headerName: getLabel("document"),
      headerClassName: "document",
      sortable: false,
      cellClassName: "document",
      width: 100,
      renderCell: (params) => {
        return params.row.documents != null ? (
          <DocumentDownload documents={params.row.documents} />
        ) : (
          <></>
        );
      },
    },
    {
      field: "actions",
      headerName: getLabel("actions"),
      headerClassName: "actions",
      sortable: false,
      cellClassName: "actions",
      width: 100,
      renderCell: (params) => {
        return (
          <ActionsMenu
            status={params.row.status}
            isOwner={true}
            continueAction={() => handleContinueAction(params.row)}
            viewSummaryAction={() => handleViewSummaryAction(params.row)}
            modifyAction={() => handleModifyAction(params.row, "Modification")}
            deleteAction={() => handleModifyAction(params.row, "Delete")}
            cancelAction={() => handleCancelAction(params.row)}
            minorChangesAction={
              isCommunicationTeam
                ? () => handleMinorChangesAction(params.row)
                : undefined
            }
          />
        );
      },
    },
  ];

  const getColumns = (): GridColDef[] => {
    if (status === "InProgress") {
      return inProgressColumns;
    } else {
      return completedColumns;
    }
  };

  useEffect(() => {
    setLoading(true);
    workflowService
      .getMyInboxPublications(status, currentFilter)
      .then((result) => {
        setPublications(result.list);
        setTotalRows(result.totalRows);
        setLoading(false);
      });
  }, [currentFilter, status, refresh]);

  const handlePageChange = (pageNumber: number) => {
    let filter = { ...currentFilter, pageNumber: pageNumber };
    setCurrentFilter(filter);
  };

  const handleSortChange = (field: string, sortDescending: boolean) => {
    let filter = {
      pageNumber: 1,
      pageSize: 5,
      sortField: field,
      sortDescending: sortDescending,
    };
    setCurrentFilter(filter);
  };

  const handleRowClick = (row: Publication) => {
    navigate(`/summary/${row.ticketNumber}`);
  };

  return (
    <Box className={styles.main}>
      <Paper
        elevation={3}
        className={styles.basic + " " + (showBack ? styles.showBack : "")}
      >
        <div className={styles.backContainer}>
          {showBack === true ? <BackButton text="back" to="/" /> : <></>}
        </div>
        <div className={styles.header}>
          <h3>{title}</h3>
          {showWorkflowTypesReference && (
            <div className={styles.workflowTypes}>
              <p>
                <WorkflowTypeComponent type="Creation" />
                {t("creation")}
              </p>
              <p>
                <WorkflowTypeComponent type="Modification" />
                {t("modification")}
              </p>
              <p>
                <WorkflowTypeComponent type="Delete" />
                {t("delete")}
              </p>
            </div>
          )}
        </div>
        <PaginatedDataTable<Publication>
          rows={publications}
          totalRows={totalRows}
          initialPage={currentFilter.pageNumber - 1}
          pageSize={currentFilter.pageSize}
          columns={getColumns()}
          handlePageChange={handlePageChange}
          handleRowClick={handleRowClick}
          handleSortChange={handleSortChange}
          emptyMessage={t("noRowsMessage", {
            entity:
              status === "InProgress"
                ? t("publicationsInProgress")
                : t("publicationsCompleted"),
          })}
          loading={loading}
        />
      </Paper>
      {modifySelected && (
        <ModifyDialog
          ticketNumber={modifySelected.ticketNumber}
          cancelAction={handleModifyCancel}
          open={Boolean(modifySelected)}
          type={modifyType}
        />
      )}
      {canRepublishChange && !canRepublishChange.canStart && (
        <RepublishWarningDialog
          mostRecentTicketNumber={canRepublishChange.mostRecentTicketNumber}
          open={true}
          close={() => {
            setCanRepublishChange(undefined);
          }}
        />
      )}
      <CancelWorkflowDialog
        open={cancelDialogShow}
        yesAction={() => cancelPublication()}
        noAction={() => setCancelDialogShow(false)}
      />
    </Box>
  );
};

export default MyInboxPublications;
